import React, { useEffect, useState, useContext } from 'react';
import {
  QtcRenderer, KeypadContext, dispatchStopReadAloudEvent, useReadAloud
} from '@3plearning/question-components-library';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Text } from '@chakra-ui/react';
import { t } from '@lingui/macro';
import PropTypes from 'prop-types';
import { updateAnswer } from './conquest.actions';
import css from './conquest.scss';
import { useLocalisation } from '../../helpers/i18n/use-localisation';
import { getValueFromUrl, randomiseQuestionMCQOptions } from '../../helpers/utils';
import { activityLoaded } from '../activity/activity-actions';
import { disableSpinner } from '../mle/mle-actions';
import { LEARNOSITY_SPINNER } from '../../helpers/constants';
import { replaceSubTypes } from './conquest.utils';
import { useMeasurementTools } from './hooks/use-measurement-tools';

const Conquest = ({ readAloudConfig }) => {
  const { i18n } = useLocalisation();
  const dispatch = useDispatch();
  const [question, setQuestion] = useState([]);
  const [disRat, setDisRat] = useState(null);
  const [keyId, setKeyId] = useState('');
  const { activityReducer, conquestReducer } = useSelector(state => state);
  const { questions } = activityReducer;
  const { tools, setupTools } = useMeasurementTools();
  const {
    keypadRef, questionBodyElement
  } = useContext(KeypadContext);
  const keypadPresetFromQueryParams = getValueFromUrl('keypadPreset');

  const feedback = ({ answer, isReady }) => {
    dispatch(updateAnswer({ answer, isReady }));
  };

  useEffect(() => {
    if (questions.length > 0 && questions[0]) {
      setQuestion(
        randomiseQuestionMCQOptions(questions[0].conquest.questionVariant.questionItem.questionContent.content)
      );
      setupTools(questions[0].conquest.questionVariant.questionItem.measurementTool);
      setKeyId(questions[0].conquest.questionVariant.id);
      dispatch(activityLoaded());
      dispatch(disableSpinner(LEARNOSITY_SPINNER));
    }
    if (
      questions.length > 0 && questions[0] &&
      questions[0].conquest.questionVariant.questionItem.distractorRationale
    ) {
      setDisRat(questions[0].conquest.questionVariant.questionItem.distractorRationale.content);
    } else {
      setDisRat(null);
    }
  }, [questions]);

  const item = '{item}';
  const target = '{target}';
  const number = '{number}';
  const total = '{total}';
  const value = '{value}';

  const qtcTranslations = {
    correct: i18n._(t`Correct`),
    incorrect: i18n._(t`Incorrect`),
    dndInstructions: i18n._(t`Press Space to pick up, then Space on an answer space to place. Escape cancels.`),
    dndPickedUp: i18n._(t`${item} picked up.`),
    dndCancelled: i18n._(t`${item} put down.`),
    dndPlaced: i18n._(t`${item} placed in ${target}.`),
    dndPlaceIn: i18n._(t`Place in ${target}`),
    dndAnswerBank: i18n._(t`Answer bank`),
    dndAnswerSpace: i18n._(t`Answer space ${number}`),
    dndNumberLineValue: i18n._(t`${value} on the number line`),
    dndNumberLinePosition: i18n._(t`Position ${number} of ${total} on the number line`)
  };

  const { setIsTranscriptModalOpen } = useReadAloud();

  const stopReadAloudAndCloseTranscript = () => {
    dispatchStopReadAloudEvent();
    setIsTranscriptModalOpen(false);
  };

  useEffect(() => {
    stopReadAloudAndCloseTranscript();

    return stopReadAloudAndCloseTranscript;
  }, [keyId]);

  return (
    <Box id={'new-content-activity'}>
      <Box key={keyId}>
        {keypadRef.current && <QtcRenderer
          question={question}
          callback={feedback}
          validation={conquestReducer.validation}
          keypadPreset={
            keypadPresetFromQueryParams ? Number(keypadPresetFromQueryParams) : 3
          }
          keypadContainer={keypadRef.current}
          readAloudConfig={readAloudConfig}
          translations={qtcTranslations}
          userResponse={conquestReducer.answer}
          measurementTools={tools}
          scrollableContainer={questionBodyElement}
        />}
      </Box>
      {
        conquestReducer.validation && !conquestReducer.validation.isCorrect && (
          <Box className={css['correct-answer']} >
            <Text as={'h2'} className={css['correct-answer-text']} >
              {i18n._(t`Here's the correct answer`)}
            </Text>
            <Box className={css['correct-answer-container']}>
              <QtcRenderer
                callback={() => undefined}
                question={question}
                showCorrectAnswer
                translations={qtcTranslations}
                validation={conquestReducer.validation}
              />
            </Box>
          </Box>
        )
      }
      {
        conquestReducer.validation && !conquestReducer.validation.isCorrect && disRat && (
          <Box className={css['dis-rat']} >
            <Box className={css['dis-rat-container']}>
              <QtcRenderer
                question={replaceSubTypes(disRat, 'default-content', 'question-description') || []}
                callback={() => undefined}
                translations={qtcTranslations}
              />
            </Box>
          </Box>
        )
      }
    </Box>
  );
};

Conquest.propTypes = {
  readAloudConfig: PropTypes.object
};

export default Conquest;
